/**
 * TSV / spreadsheet parser for the "Import from spreadsheet" modal.
 *
 * Expected paste shape (tabs between columns, newlines between rows):
 *   Customer name \t Number \t Email \t Deposit Paid \t Payment Method
 *     \t Booking Date \t Balance on booking \t Notes \t Final Balance
 *
 * Real-world quirks the parser must tolerate:
 *   - Header row is optional; may or may not be pasted with the data
 *   - Rows can be entirely empty between logical blocks (Google Sheets
 *     often exports trailing tabs → empty cells)
 *   - Guest rows have only a name, everything else blank OR the row
 *     duplicates the primary's contact but lacks deposit info. Rule:
 *     **any row whose name matches a prior primary is treated as a
 *     guest of that primary**, regardless of the other cells.
 *   - Guest name may be given in parens: "Shahid Mahmood (Sohail Hussain)".
 *     Store the outer as the primary name link + the inner as the actual
 *     attendee name so staff can see both on the register.
 *
 * The parser is intentionally forgiving — everything is editable in the
 * preview table before submit, so we err on the side of "make a
 * reasonable guess" rather than reject malformed rows outright.
 */

// Column indices matching the user's spreadsheet layout.
const COLS = {
  name: 0,
  phone: 1,
  email: 2,
  deposit_paid: 3,
  payment_method: 4,
  booking_date: 5,
  balance_on_booking: 6,
  notes: 7,
  final_balance: 8,
  payment_log: 9,  // Optional col J — explicit payment log entries
};

const MONTHS = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3,
  apr: 4, april: 4, may: 5, jun: 6, june: 6, jul: 7, july: 7,
  aug: 8, august: 8, sep: 9, sept: 9, september: 9, oct: 10, october: 10,
  nov: 11, november: 11, dec: 12, december: 12,
};

/** Parse "Mon 7th & Tue 8th September" (or "7th September 2026") to an ISO date.
 *  When the year is missing, assume the NEXT occurrence from today. */
export function parseCourseTitle(line) {
  if (!line) return { name: '', date: '', sections: [] };
  const trimmed = String(line).trim();

  const sections = [];
  const lower = trimmed.toLowerCase();
  if (lower.includes('probound') || lower.includes('bound')) sections.push('probound');
  if (lower.includes('proepoxy') || lower.includes('epoxy')) sections.push('proepoxy');
  if (lower.includes('prodecor') || lower.includes('decor')) sections.push('prodecor');

  // Look for ALL "<num><st|nd|rd|th> <month>" occurrences and pick the
  // earliest — the user's spreadsheet header often reads "Mon 7th & Tue
  // 8th September" for a 2-day course; the start-date is what staff want.
  const dayMatches = [...trimmed.matchAll(/(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]+)(?:\s+(\d{4}))?/g)];
  let iso = '';
  // A month can be "shared" across earlier day tokens (e.g. "7th & 8th
  // September" — the 7th belongs to September too). Grab any bare-day
  // tokens that appear before the first match with a month, and reuse
  // that month.
  let sharedMonth = null;
  let sharedYear = null;
  for (const m of dayMatches) {
    const monthName = m[2].toLowerCase();
    if (MONTHS[monthName]) {
      sharedMonth = MONTHS[monthName];
      sharedYear = m[3] ? parseInt(m[3], 10) : null;
      break;
    }
  }
  if (sharedMonth) {
    // All bare-number "<num>(st|nd|rd|th)?" tokens in the header (in order).
    const bareDays = [...trimmed.matchAll(/\b(\d{1,2})(?:st|nd|rd|th)\b/g)]
      .map((m) => parseInt(m[1], 10))
      .filter((n) => n >= 1 && n <= 31);
    const day = bareDays.length ? Math.min(...bareDays) : null;
    if (day) {
      const now = new Date();
      let year = sharedYear || now.getFullYear();
      if (!sharedYear) {
        const candidate = new Date(year, sharedMonth - 1, day);
        if (candidate < now) year += 1;
      }
      iso = `${year}-${String(sharedMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }

  return { name: trimmed, date: iso, sections };
}

/** True if the row looks like a header (contains "customer" or "email"). */
/** True when pasted text looks like the booking spreadsheet (Name · Phone · Email · Deposit …)
 *  rather than the simple `name, email, phone` CSV. */
export function looksLikeBookingSheet(text) {
  const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n').filter((l) => l.trim());
  if (lines.some((l) => isHeaderRow(l.split('\t')))) return true;
  const data = lines.filter((l) => l.includes('\t'));
  if (!data.length) return false;
  const emailAt = (cells, i) => /@/.test(cells[i] || '');
  const hits = data.filter((l) => { const c = l.split('\t'); return c.length >= 4 && emailAt(c, 2) && !emailAt(c, 1); });
  return hits.length > 0 && hits.length >= data.length / 2;
}

function isHeaderRow(cells) {
  const first = (cells[0] || '').toLowerCase();
  return first.includes('customer name') || first.includes('name') && cells.some((c) => (c || '').toLowerCase().includes('email'));
}

/** Currency-string → number ("£120.00" → 120, "£1,080.00" → 1080). Returns
 *  null when there's no meaningful value so downstream fields stay unset
 *  (as opposed to defaulting to 0 and looking like a payment). */
export function parseCurrency(raw) {
  if (raw === null || raw === undefined) return null;
  const s = String(raw).replace(/[£,\s]/g, '').trim();
  if (s === '' || s === '-') return null;
  const n = parseFloat(s);
  if (Number.isNaN(n)) return null;
  return n;
}

/** Split a name into { display, guestActual } — the outer text is the
 *  known booker/primary name; the parens content is the actual attendee. */
function splitParens(raw) {
  const s = String(raw || '').trim();
  const m = s.match(/^(.+?)\s*\(\s*(.+?)\s*\)\s*$/);
  if (!m) return { display: s, guestActual: null };
  return { display: m[1].trim(), guestActual: m[2].trim() };
}

/** Normalise for name-matching — lowercase, collapse spaces. */
function normaliseName(s) {
  return String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/** DD/MM(/YYYY) booking-date cell → ISO YYYY-MM-DD. Nulls when unparseable. */
function parseBookingDateAsIso(raw) {
  return parseUkDate(raw, new Date().getFullYear());
}

/** Simple email validity. Empty is allowed (guest seats). */
function isValidEmail(s) {
  if (!s) return true;
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(s).trim());
}

/** Canonicalise the payment method text staff type in the sheet. Keeps
 *  the register's payment history readable + groupable regardless of
 *  whether the row said "BT", "bt", "Bank Transfer" or "Xero/Invoice". */
function canonicaliseMethod(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;
  const lower = s.toLowerCase();
  if (/(bt|bank\s*transfer)\b/.test(lower)) return 'Bank Transfer';
  if (/(wp\b|world\s*pay)/.test(lower)) return 'Worldpay';
  if (/xero/.test(lower)) return 'Xero Invoice';
  if (/invoice/.test(lower)) return 'Invoice';
  if (/cash/.test(lower)) return 'Cash';
  if (/n\/?a/i.test(lower)) return null;
  // Otherwise keep the original text so staff-typed methods aren't lost.
  return s;
}

/** DD/MM (year optional) → ISO YYYY-MM-DD. Uses the course date's year
 *  when a bare "20/08" is given (falls back to current year otherwise). */
function parseUkDate(raw, fallbackYear) {
  const s = String(raw || '').trim();
  if (!s) return null;
  const m = s.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/);
  if (!m) return null;
  const day = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  if (day < 1 || day > 31 || month < 1 || month > 12) return null;
  let year;
  if (m[3]) {
    year = parseInt(m[3], 10);
    if (year < 100) year += year >= 70 ? 1900 : 2000;
  } else {
    year = fallbackYear || new Date().getFullYear();
  }
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Parse a "Payment log" cell (column J) into individual payment entries.
 * Format is one entry per `;`-separated chunk. Each chunk is a loose
 * `£amount method date` triplet — amount is REQUIRED, method + date
 * are best-effort. Examples the parser handles:
 *   "£120 Worldpay 21/04/2026; £354 BT 07/09/2026"
 *   "120 BT 21/04; 354 bank transfer 07/09"
 *   "£708 BT" (no date — falls back to caller's default)
 */
function parsePaymentLogCell(raw, defaultDate) {
  const s = String(raw || '').trim();
  if (!s) return [];
  const fallbackYear = defaultDate ? parseInt(defaultDate.slice(0, 4), 10) : null;
  const chunks = s.split(/[;\n]/).map((c) => c.trim()).filter(Boolean);
  const entries = [];
  for (const chunk of chunks) {
    // Grab the amount first — required. Everything after is method + date.
    const amountMatch = chunk.match(/£?\s*(\d+(?:[.,]\d+)?)/);
    if (!amountMatch) continue;
    const amount = parseFloat(amountMatch[1].replace(',', '.'));
    if (!Number.isFinite(amount) || amount <= 0) continue;
    const remainder = chunk.slice(amountMatch.index + amountMatch[0].length).trim();
    // Try to extract a UK date at the tail of the remainder.
    const dateMatch = remainder.match(/(\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)\s*$/);
    let iso = null;
    let methodText = remainder;
    if (dateMatch) {
      iso = parseUkDate(dateMatch[1], fallbackYear);
      methodText = remainder.slice(0, dateMatch.index).trim();
    }
    entries.push({
      amount: Math.round(amount * 100) / 100,
      method: canonicaliseMethod(methodText),
      at: iso || defaultDate || null,
    });
  }
  return entries;
}

/**
 * Best-effort scan of the free-text Notes cell for completed payments.
 * Only extracts entries that look like a payment was already made — never
 * future promises (skips things like "will pay Fri 21/08" or "sent
 * invoice for balance"). Conservative by design because Notes is noisy.
 *
 *   "Paid £708 BT 20/08"         → {708, Bank Transfer, 20/08}
 *   "Paid £120 on 20/08 by WP"   → {120, Worldpay, 20/08}
 *   "Paid balance BT"            → {balanceHint, Bank Transfer, null}
 *
 * `balanceHint` is the balance-on-booking figure — the parser passes it
 * in so "Paid balance BT" can be attributed a real number.
 */
function parseNotesForPayments(notes, { balanceHint, defaultDate }) {
  const s = String(notes || '').trim();
  if (!s) return [];
  // Bail early on obvious "future payment" phrasing so we don't
  // accidentally log an expected payment as a real one.
  const futureHints = /\b(will\s+pay|paying\s+on|paying\s+by|paying\s+cash\s+on\s+the\s+day|cash\s+on\s+the\s+day|being\s+paid\s+by|sent\s+invoice|needs\s+to\s+pay|owe)\b/i;
  const fallbackYear = defaultDate ? parseInt(defaultDate.slice(0, 4), 10) : null;
  const entries = [];
  // Split on ' - ' and '.' so multiple sentences in one cell can each be
  // scanned separately. "Paid £708 BT 20/08 - CASH ON THE DAY" → the
  // first half becomes an entry, second half is filtered as future.
  const parts = s.split(/\s+[-.·]\s+|\.\s+/);
  for (const part of parts) {
    if (!/paid/i.test(part)) continue;
    if (futureHints.test(part) && !/paid\s+£/i.test(part)) continue;
    // "Paid £708 BT 20/08" or "Paid balance BT 20/08" or "Paid balance BT"
    const amountMatch = part.match(/paid\s+£?\s*(\d+(?:[.,]\d+)?)/i);
    let amount = null;
    let rest = part.replace(/^\s*paid\s+/i, '').trim();
    if (amountMatch) {
      amount = parseFloat(amountMatch[1].replace(',', '.'));
      rest = part.slice(amountMatch.index + amountMatch[0].length).trim();
    } else if (/paid\s+balance/i.test(part)) {
      if (balanceHint && balanceHint > 0) amount = balanceHint;
      rest = part.replace(/^\s*paid\s+balance\s*/i, '').trim();
    }
    if (!Number.isFinite(amount) || amount <= 0) continue;
    // Trim leading "via " noise so canonicaliseMethod handles cleanly.
    rest = rest.replace(/^via\s+/i, '');
    const dateMatch = rest.match(/(\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)/);
    let iso = null;
    let methodText = rest;
    if (dateMatch) {
      iso = parseUkDate(dateMatch[1], fallbackYear);
      // Remove the date substring from the method text without
      // reordering the remainder.
      methodText = (rest.slice(0, dateMatch.index) + rest.slice(dateMatch.index + dateMatch[0].length)).trim();
    }
    entries.push({
      amount: Math.round(amount * 100) / 100,
      method: canonicaliseMethod(methodText) || null,
      at: iso || null,
    });
  }
  return entries;
}

// Exported for pytest-style unit tests + backend parity mirror.
export { canonicaliseMethod, parsePaymentLogCell, parseNotesForPayments };

/**
 * Compute the payment math + payment_history for a single parsed row.
 * Pure function: same inputs → same outputs, no side effects. Used by
 * BOTH `parseSpreadsheet` (so the preview table can display the entries
 * before submit) AND `buildImportPayload` (so the backend receives the
 * same numbers). Any change here flows to both places automatically.
 */
export function computeRowPayments(row) {
  const deposit = parseCurrency(row.deposit_paid);
  const balanceAtBooking = parseCurrency(row.balance_on_booking);
  const finalBalanceRaw = parseCurrency(row.final_balance);
  // If final_balance is missing but balance_on_booking is provided, fall
  // back so older pastes without col I still work.
  const finalBalance = finalBalanceRaw !== null
    ? finalBalanceRaw
    : (balanceAtBooking !== null ? balanceAtBooking : null);

  let paymentTotal = null;
  if (deposit !== null && balanceAtBooking !== null) {
    paymentTotal = Math.round((deposit + balanceAtBooking) * 100) / 100;
  } else if (deposit !== null && finalBalance !== null) {
    paymentTotal = Math.round((deposit + finalBalance) * 100) / 100;
  }

  let paymentReceived = deposit;
  const balanceOwed = finalBalance;
  let paidNow = false;
  if (paymentTotal !== null && balanceOwed !== null) {
    paymentReceived = Math.round((paymentTotal - balanceOwed) * 100) / 100;
    if (paymentReceived < 0) paymentReceived = deposit;
    if (balanceOwed <= 0) paidNow = true;
  } else if (paymentTotal === null && balanceOwed === 0) {
    paidNow = true;
  }

  // Build payment_history (deposit auto → col J → Notes fallback → dedupe).
  const bookingIso = parseBookingDateAsIso(row.booking_date);
  const history = [];
  if (deposit !== null && deposit > 0) {
    history.push({
      amount: deposit,
      method: canonicaliseMethod(row.payment_method) || null,
      at: bookingIso || null,
      source: 'deposit',
    });
  }
  const logEntries = parsePaymentLogCell(row.payment_log, bookingIso);
  for (const e of logEntries) history.push({ ...e, source: 'log' });
  if (logEntries.length === 0) {
    const notesEntries = parseNotesForPayments(row.notes, {
      balanceHint: balanceAtBooking,
      defaultDate: bookingIso,
    });
    for (const e of notesEntries) history.push({ ...e, source: 'notes' });
  }
  const seen = new Set();
  const dedupedHistory = history.filter((entry) => {
    const key = `${entry.amount}|${entry.at || ''}|${entry.method || ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Keep the itemised history in step with "Paid so far": if the sheet's
  // Final Balance says more has been paid than the Notes itemise (e.g.
  // "Paid rest 16/09" with no amount), log the difference as one entry so
  // any later edit that re-sums the history can't silently reopen a balance.
  const itemised = Math.round(dedupedHistory.reduce((a, e) => a + Number(e.amount || 0), 0) * 100) / 100;
  const gap = paymentReceived !== null ? Math.round((paymentReceived - itemised) * 100) / 100 : 0;
  if (gap > 0) {
    dedupedHistory.push({ amount: gap, method: null, at: null, source: 'sheet', note: 'Remaining balance per spreadsheet' });
  }

  return {
    paymentTotal,
    paymentReceived,
    balanceOwed,
    paidNow,
    paymentHistory: dedupedHistory,
  };
}

/**
 * Main entry point — parses a TSV block into structured rows for the
 * preview table. Returns `{ courseTitle, rows: [] }`.
 *
 * Each row shape:
 *   {
 *     include: true,           // preview checkbox default
 *     rawName: string,
 *     name: string,             // outer name (matches primary if guest)
 *     phone, email,
 *     deposit_paid, payment_method, booking_date,
 *     balance_on_booking, notes, final_balance,
 *     is_guest: bool,
 *     guest_of_index: number|null,  // pointer into the returned rows
 *     guest_actual_name: string|null,
 *     issues: string[],         // amber flags shown in the preview
 *   }
 */
export function parseSpreadsheet(text) {
  const lines = String(text || '').replace(/\r\n?/g, '\n').split('\n');

  // First non-empty line looks like a course-title header if it doesn't
  // contain tabs (i.e. it's not a data row). Consume it separately.
  let courseTitle = '';
  let i = 0;
  while (i < lines.length && lines[i].trim() === '') i++;
  // A title row copied from Sheets arrives as "Title\t\t\t…" — only the first cell filled.
  if (i < lines.length) {
    const cells = lines[i].split('\t');
    if (cells.length === 1 || (cells[0].trim() && cells.slice(1).every((c) => !c.trim()) && !cells[0].includes('@'))) {
      courseTitle = lines[i].trim();
      i += 1;
    }
  }

  const rawRows = [];
  for (; i < lines.length; i += 1) {
    const line = lines[i];
    if (line.trim() === '') continue;
    const cells = line.split('\t');
    if (isHeaderRow(cells)) continue;
    rawRows.push(cells);
  }

  const rows = [];
  const primaryIndexByName = new Map();

  rawRows.forEach((cells) => {
    const rawName = (cells[COLS.name] || '').trim();
    if (!rawName) return; // completely blank row — skip

    const phone = (cells[COLS.phone] || '').trim();
    const email = (cells[COLS.email] || '').trim().toLowerCase();
    const deposit_paid = (cells[COLS.deposit_paid] || '').trim();
    const payment_method = (cells[COLS.payment_method] || '').trim();
    const booking_date = (cells[COLS.booking_date] || '').trim();
    const balance_on_booking = (cells[COLS.balance_on_booking] || '').trim();
    const notes = (cells[COLS.notes] || '').trim();
    const final_balance = (cells[COLS.final_balance] || '').trim();
    const payment_log = (cells[COLS.payment_log] || '').trim();

    const { display: primaryName, guestActual } = splitParens(rawName);
    const normalised = normaliseName(primaryName);

    // Heuristic: row is a guest iff we've already seen a primary with the
    // same name (with or without parens). If they haven't, this is a new
    // primary regardless of whether other cells are blank — the register
    // needs SOME primary or we can't link the guests later.
    const priorPrimaryIndex = primaryIndexByName.get(normalised);
    const isGuest = priorPrimaryIndex !== undefined;

    const issues = [];
    if (email && !isValidEmail(email)) issues.push('bad_email');
    if (!isGuest && !phone && !email) issues.push('no_contact');

    const row = {
      include: true,
      rawName,
      name: primaryName,
      phone,
      email,
      deposit_paid,
      payment_method,
      booking_date,
      balance_on_booking,
      notes,
      final_balance,
      payment_log,
      is_guest: isGuest,
      guest_of_index: isGuest ? priorPrimaryIndex : null,
      guest_actual_name: guestActual,
      issues,
    };
    // Attach computed payment fields (history + totals) so the preview
    // table can render them and the payload builder can reuse them
    // without re-parsing. Recomputed on any row edit — see PreviewStep.
    Object.assign(row, computeRowPayments(row));
    const currentIndex = rows.length;
    rows.push(row);
    if (!isGuest) primaryIndexByName.set(normalised, currentIndex);
  });

  return { courseTitle, rows };
}

/** Build the JSON payload the backend `POST /import` endpoint expects. */
export function buildImportPayload({ course, rows }) {
  const included = rows.filter((r) => r.include);
  // Rebuild guest_of_index against the FILTERED array positions.
  const primaryOldToNew = new Map();
  const filtered = [];
  included.forEach((r, newIdx) => {
    if (!r.is_guest) {
      // find original index by identity
      const oldIdx = rows.indexOf(r);
      primaryOldToNew.set(oldIdx, newIdx);
    }
    filtered.push(r);
  });

  const trainees = filtered.map((r) => {
    const oldGuestOf = r.guest_of_index;
    const newGuestOf = oldGuestOf !== null && oldGuestOf !== undefined
      ? primaryOldToNew.get(oldGuestOf)
      : null;
    // Payment fields were pre-computed by `parseSpreadsheet` (and kept
    // fresh by PreviewStep's onUpdateRow via `computeRowPayments`). We
    // just marshal them onto the payload shape here.
    const p = computeRowPayments(r); // recompute defensively in case the row was edited
    return {
      name: r.name,
      phone: r.phone || null,
      email: r.email || null,
      is_guest: !!r.is_guest,
      guest_of_index: (r.is_guest && (newGuestOf === undefined ? null : newGuestOf)) ?? null,
      guest_actual_name: r.guest_actual_name || null,
      payment_total: p.paymentTotal !== null ? p.paymentTotal : undefined,
      payment_received: p.paymentReceived !== null ? p.paymentReceived : undefined,
      balance_owed: p.balanceOwed !== null ? p.balanceOwed : undefined,
      paid_now: p.paidNow || undefined,
      payment_history: p.paymentHistory.length > 0 ? p.paymentHistory : undefined,
      booking_details: {
        deposit_paid: r.deposit_paid || null,
        payment_method: r.payment_method || null,
        booking_date: r.booking_date || null,
        balance_on_booking: r.balance_on_booking || null,
        notes: r.notes || null,
        final_balance: r.final_balance || null,
      },
    };
  });

  return {
    course: {
      name: course.name.trim(),
      date: course.date,
      sections: course.sections,
    },
    trainees,
  };
}
